// Local KML/KMZ overlays. Files are parsed entirely in the browser and never
// sent to the server. Used by the burger menu (load/manage) and the map (render).

export type LatLng = { lat: number; lng: number }

export type KmlFeature =
  | { kind: "point"; name?: string; description?: string; position: LatLng }
  | { kind: "line"; name?: string; description?: string; path: LatLng[] }
  | { kind: "polygon"; name?: string; description?: string; rings: LatLng[][] } // rings[0] = outer, rest = holes

export type LocalOverlay = {
  id: string
  name: string
  visible: boolean
  color: string // hex, drives fill/line/point rendering on the map
  opacity: number // 0..1, user-adjustable render opacity
  features: KmlFeature[]
}

// Default colors handed to newly loaded overlays, rotated so back-to-back
// uploads land on visually distinct colors. Users can override per file.
export const OVERLAY_PALETTE = [
  "#FFC72C", // brand yellow
  "#E4572E", // red-orange
  "#3CAEA3", // teal
  "#8367C7", // purple
  "#2A9D8F", // green
  "#1982C4", // blue
]

// ---- KML parsing (DOMParser, no dependency) -------------------------------

export function parseKml(kmlText: string): KmlFeature[] {
  const doc = new DOMParser().parseFromString(kmlText, "text/xml")
  if (doc.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Invalid KML/XML")
  }
  const features: KmlFeature[] = []
  for (const pm of Array.from(doc.getElementsByTagName("Placemark"))) {
    const name = directChildText(pm, "name")
    const description = placemarkDescription(pm)
    collectGeometry(pm, name, description, features)
  }
  return features
}

// Human-readable secondary text for a placemark (e.g. an address). Prefer the
// KML <description>; fall back to ExtendedData fields Google My Maps commonly
// uses for addresses. HTML is stripped so it renders cleanly in a tooltip.
function placemarkDescription(pm: Element): string | undefined {
  const desc = stripHtml(directChildText(pm, "description"))
  if (desc) return desc
  for (const data of Array.from(pm.getElementsByTagName("Data"))) {
    const key = data.getAttribute("name")?.toLowerCase() ?? ""
    if (/address|location|adresse|dirección|direccion/.test(key)) {
      const val = descendantText(data, "value")?.trim()
      if (val) return stripHtml(val)
    }
  }
  return undefined
}

// Strip HTML tags/entities down to a single line of text.
function stripHtml(s: string | undefined): string | undefined {
  if (!s) return undefined
  const doc = new DOMParser().parseFromString(s, "text/html")
  const text = (doc.body.textContent ?? "").replace(/\s+/g, " ").trim()
  return text || undefined
}

function collectGeometry(
  pm: Element,
  name: string | undefined,
  description: string | undefined,
  out: KmlFeature[],
) {
  // MultiGeometry nests these, so a descendant query naturally flattens it.
  for (const pt of Array.from(pm.getElementsByTagName("Point"))) {
    const c = parseCoords(descendantText(pt, "coordinates"))
    if (c[0]) out.push({ kind: "point", name, description, position: c[0] })
  }
  for (const ls of Array.from(pm.getElementsByTagName("LineString"))) {
    const c = parseCoords(descendantText(ls, "coordinates"))
    if (c.length >= 2) out.push({ kind: "line", name, description, path: c })
  }
  for (const poly of Array.from(pm.getElementsByTagName("Polygon"))) {
    const rings: LatLng[][] = []
    const outer = poly.getElementsByTagName("outerBoundaryIs")[0]
    if (outer) {
      const c = parseCoords(descendantText(outer, "coordinates"))
      if (c.length >= 3) rings.push(c)
    }
    for (const inner of Array.from(poly.getElementsByTagName("innerBoundaryIs"))) {
      const c = parseCoords(descendantText(inner, "coordinates"))
      if (c.length >= 3) rings.push(c)
    }
    if (rings.length) out.push({ kind: "polygon", name, description, rings })
  }
}

// KML coordinates: whitespace-separated "lng,lat[,alt]" tuples.
function parseCoords(s: string | undefined): LatLng[] {
  if (!s) return []
  return s
    .trim()
    .split(/\s+/)
    .map((tok) => {
      const parts = tok.split(",")
      return { lng: Number(parts[0]), lat: Number(parts[1]) }
    })
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
}

function directChildText(el: Element, tag: string): string | undefined {
  for (const child of Array.from(el.children)) {
    if (child.tagName === tag) return child.textContent?.trim() || undefined
  }
  return undefined
}

function descendantText(el: Element, tag: string): string | undefined {
  return el.getElementsByTagName(tag)[0]?.textContent ?? undefined
}

// ---- KMZ extraction (zero-dep zip reader + DecompressionStream) -----------

// A KMZ is a zip archive containing a .kml document (usually doc.kml) plus
// optional assets. We read the central directory, locate the .kml entry and
// inflate it. Raw DEFLATE is handled by the browser's DecompressionStream.
export async function extractKmlFromKmz(buf: ArrayBuffer): Promise<string> {
  const view = new DataView(buf)
  const bytes = new Uint8Array(buf)

  const eocd = findEocd(view)
  if (eocd < 0) throw new Error("Not a valid KMZ (no end-of-central-directory)")
  const entryCount = view.getUint16(eocd + 10, true)
  const cdOffset = view.getUint32(eocd + 16, true)

  type Entry = { name: string; method: number; compSize: number; localOffset: number }
  let firstKml: Entry | null = null
  let docKml: Entry | null = null

  let ptr = cdOffset
  for (let i = 0; i < entryCount; i++) {
    if (view.getUint32(ptr, true) !== 0x02014b50) break // central dir header sig
    const method = view.getUint16(ptr + 10, true)
    const compSize = view.getUint32(ptr + 20, true)
    const nameLen = view.getUint16(ptr + 28, true)
    const extraLen = view.getUint16(ptr + 30, true)
    const commentLen = view.getUint16(ptr + 32, true)
    const localOffset = view.getUint32(ptr + 42, true)
    const name = new TextDecoder().decode(bytes.subarray(ptr + 46, ptr + 46 + nameLen))
    if (name.toLowerCase().endsWith(".kml")) {
      const entry: Entry = { name, method, compSize, localOffset }
      if (!firstKml) firstKml = entry
      if (/(^|\/)doc\.kml$/i.test(name)) docKml = entry
    }
    ptr += 46 + nameLen + extraLen + commentLen
  }

  const entry = docKml || firstKml
  if (!entry) throw new Error("No .kml file found inside KMZ")

  // Local file header lengths can differ from the central directory, so read
  // them here to find where the compressed data actually starts.
  const lo = entry.localOffset
  if (view.getUint32(lo, true) !== 0x04034b50) throw new Error("Bad local file header")
  const localNameLen = view.getUint16(lo + 26, true)
  const localExtraLen = view.getUint16(lo + 28, true)
  const dataStart = lo + 30 + localNameLen + localExtraLen
  const compData = bytes.subarray(dataStart, dataStart + entry.compSize)

  if (entry.method === 0) return new TextDecoder().decode(compData)
  if (entry.method === 8) return inflateRaw(compData)
  throw new Error(`Unsupported KMZ compression method ${entry.method}`)
}

function findEocd(view: DataView): number {
  // EOCD signature 0x06054b50; scan backwards (comment field is variable length).
  const min = Math.max(0, view.byteLength - 65557)
  for (let i = view.byteLength - 22; i >= min; i--) {
    if (view.getUint32(i, true) === 0x06054b50) return i
  }
  return -1
}

async function inflateRaw(data: Uint8Array): Promise<string> {
  const ds = new (window as any).DecompressionStream("deflate-raw")
  const stream = new Blob([data]).stream().pipeThrough(ds)
  const ab = await new Response(stream).arrayBuffer()
  return new TextDecoder().decode(ab)
}

// ---- Loading a File into an overlay ---------------------------------------

export async function overlayFromFile(file: File, color?: string): Promise<LocalOverlay> {
  const isKmz = file.name.toLowerCase().endsWith(".kmz")
  const kmlText = isKmz ? await extractKmlFromKmz(await file.arrayBuffer()) : await file.text()
  const features = parseKml(kmlText)
  if (!features.length) throw new Error("No placemarks found in file")
  return {
    id: makeId(file.name),
    name: file.name,
    visible: true,
    color: color ?? OVERLAY_PALETTE[0],
    opacity: 1,
    features,
  }
}

// ---- Google My Maps links -------------------------------------------------

// Accept anything a user is likely to paste from Google My Maps and pull out
// the map id (`mid`): viewer/edit/embed links, or a bare id on its own.
export function parseMyMapsMid(input: string): string | undefined {
  const s = input.trim()
  if (!s) return undefined
  // A bare mid (no scheme, no path) — My Maps ids are URL-safe base64-ish.
  if (/^[\w-]{20,}$/.test(s) && !s.includes("/")) return s
  try {
    const url = new URL(s)
    const mid = url.searchParams.get("mid")
    if (mid) return mid
  } catch {
    // not a URL, fall through
  }
  const m = s.match(/[?&]mid=([\w-]+)/)
  return m ? m[1] : undefined
}

// Load a public Google My Maps map as an overlay. My Maps exposes a KML export
// endpoint that sends `Access-Control-Allow-Origin: *`, so this works directly
// from the browser (no proxy) as long as the map's sharing is set to public.
// `forcekml=1` returns inline KML rather than a KMZ wrapper.
export async function overlayFromMyMapsUrl(input: string, color?: string): Promise<LocalOverlay> {
  const mid = parseMyMapsMid(input)
  if (!mid) throw new Error("Not a Google My Maps link (no map id found)")
  const res = await fetch(`https://www.google.com/maps/d/kml?mid=${encodeURIComponent(mid)}&forcekml=1`)
  if (!res.ok) throw new Error(`Google returned HTTP ${res.status} (is the map shared publicly?)`)
  const kmlText = await res.text()
  const doc = new DOMParser().parseFromString(kmlText, "text/xml")
  const features = parseKml(kmlText)
  if (!features.length) throw new Error("No placemarks found in this map")
  const mapName = doc.getElementsByTagName("Document")[0]?.getElementsByTagName("name")[0]?.textContent?.trim()
  return {
    id: makeId(mid),
    name: mapName || `My Maps ${mid.slice(0, 8)}`,
    visible: true,
    color: color ?? OVERLAY_PALETTE[0],
    opacity: 1,
    features,
  }
}

// ---- Point-in-polygon (which collars fall inside a KML shape) -------------

// Ray-casting test for a single ring. Planar in lng/lat, which is fine for
// pasture-scale areas (don't trust it across the antimeridian or at the poles).
function pointInRing(p: LatLng, ring: LatLng[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].lng
    const yi = ring[i].lat
    const xj = ring[j].lng
    const yj = ring[j].lat
    const intersect =
      (yi > p.lat) !== (yj > p.lat) && p.lng < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

// Inside the outer ring and outside every hole.
export function pointInPolygonFeature(p: LatLng, f: KmlFeature): boolean {
  if (f.kind !== "polygon" || !f.rings.length) return false
  if (!pointInRing(p, f.rings[0])) return false
  for (let i = 1; i < f.rings.length; i++) {
    if (pointInRing(p, f.rings[i])) return false
  }
  return true
}

// Test a set of points against every polygon in the given overlays.
// `points` is generic so callers map their own collar shape into { id, position }.
export function pointsInsideOverlays<T extends { id: string | number; position: LatLng }>(
  points: T[],
  overlays: LocalOverlay[],
  visibleOnly = true,
): { point: T; overlay: string; shape?: string }[] {
  const hits: { point: T; overlay: string; shape?: string }[] = []
  for (const overlay of overlays) {
    if (visibleOnly && !overlay.visible) continue
    const polygons = overlay.features.filter((f) => f.kind === "polygon")
    for (const point of points) {
      for (const poly of polygons) {
        if (pointInPolygonFeature(point.position, poly)) {
          hits.push({ point, overlay: overlay.name, shape: poly.name })
          break // count each point once per overlay
        }
      }
    }
  }
  return hits
}

function makeId(name: string): string {
  const rnd =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.round(Math.random() * 1e9)}`
  return `${name}-${rnd}`
}
